'use client';

import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { FlameGlow, rem } from './SeasonalLayers';
import styles from './seasonal.module.css';

/**
 * Background of the Halloween promo popup, layer by layer from the Figma
 * frames "TESTE · Halloween popup desktop / mobile · lápide com logo IQ"
 * (12770:42307, 971x552 and 12770:42359, 400x554 of "IQ Capital | New UI
 * 2026"). The designer reworked both frames after the handoff (the cutout
 * grew and moved, the mobile frame lost 32px and gained a second darkening
 * gradient), so the handoff's flattened `arte@2x` plates in
 * public/previews/popups-handoff/hw-popup-* no longer match and are not used.
 *
 * Three kinds of layer, bottom to top:
 *  - `base-*.webp`: everything below the cutout, rasterised once at 2x in
 *    Chromium from the frame's own CSS — the Popup gradient, the 10%
 *    exclusion grain, the 7% light beams, the blurred vignette, the 11%
 *    colour-dodge fresco, the 3px-blurred scene and its 45% darkening (plus
 *    the lighten ellipse and the left fade on mobile). All static, so one
 *    image replaces a dozen blended layers and ~16MB of source textures.
 *  - the cutout ("elemento nítido": the sharp tombstone and pumpkin), its own
 *    <img> at the Figma box, and the handoff's flame glow in the same box.
 *    `glow.webp` was remapped onto the cutout's 1024x574 canvas, so with
 *    `object-fit: cover` on both the glow stays on the pumpkin at any size.
 *  - the darkening gradients and the blurred edge strips above the cutout,
 *    in CSS with the frame's values.
 *
 * Sizes are Figma px / 8 (1rem = 8px at the design width). The backdrop
 * blurs stay in px like the countdown boxes' — they do not scale with rem.
 */

const BASE = '/seasonal/halloween/popup';

/** Figma box of the cutout: desktop (0, -36) 1020.634x573.773, mobile (68.35, -50) 562.781x314.951. */
const CUTOUT: Record<'desktop' | 'mobile', CSSProperties> = {
  desktop: { left: 0, top: rem(-36), width: rem(1020.634), height: rem(573.773) },
  mobile: { left: rem(68.35), top: rem(-50), width: rem(562.781), height: rem(314.951) },
};

const EDGE_BLUR = 'blur(2.631px)';
/** 12770:42354 / 42376 / 42377: `bg-gradient-to-l` on a rect rotated 180° and flipped, i.e. mirrored. */
const EDGE_GRADIENT = 'linear-gradient(to right, rgba(0,16,11,0.05), rgba(0,16,11,0))';

function Cutout({ variant }: { variant: 'desktop' | 'mobile' }) {
  return (
    <div className="absolute" style={CUTOUT[variant]} data-seasonal-layer="cutout">
      {/* eslint-disable-next-line @next/next/no-img-element -- Figma image fill */}
      <img
        src={`${BASE}/elemento.webp`}
        alt=""
        className="absolute inset-0 h-full w-full max-w-none object-cover"
      />
      <FlameGlow src={`${BASE}/glow.webp`} delayMs={900} seed={7} />
    </div>
  );
}

export default function HalloweenPopupScene() {
  return (
    <>
      {/* ---------------- Mobile · 12770:42360 (384x538) ---------------- */}
      <div className="absolute inset-0 lg:hidden">
        <div className={cn('absolute inset-0', styles.artIn)}>
          {/* eslint-disable-next-line @next/next/no-img-element -- rasterised Figma layers */}
          <img
            src={`${BASE}/base-mobile.webp`}
            alt=""
            className="absolute inset-0 h-full w-full max-w-none object-cover"
          />
          <Cutout variant="mobile" />
        </div>
        {/* 12770:42375 escurecer base (texto): (0, 108) 386x430 */}
        <div
          className="absolute left-0"
          style={{
            top: rem(108),
            width: rem(386),
            height: rem(430),
            background:
              'linear-gradient(to bottom, rgba(5,13,11,0) 0%, rgba(5,13,11,0.8) 40%, rgba(5,13,11,0.95) 100%)',
          }}
        />
        {/* 13031:3569 escurecer base (texto): (0, 190) 386x348 */}
        <div
          className="absolute left-0"
          style={{
            top: rem(190),
            width: rem(386),
            height: rem(348),
            background:
              'linear-gradient(to bottom, rgba(5,13,11,0) 0%, rgba(5,13,11,0.79) 17.721%, rgba(5,13,11,0.94) 44.302%)',
          }}
        />
        {/* 12770:42376 / 42377: two blurred strips on the left edge, 12770:42378 on the right */}
        <div
          className="absolute"
          style={{ left: rem(-48), top: rem(-120), width: rem(76), height: rem(1023), backdropFilter: EDGE_BLUR, background: EDGE_GRADIENT }}
        />
        <div
          className="absolute"
          style={{ left: rem(-48), top: rem(-120), width: rem(83), height: rem(1023), backdropFilter: EDGE_BLUR, background: EDGE_GRADIENT }}
        />
        <div
          className="absolute"
          style={{
            left: rem(347),
            top: rem(-120),
            width: rem(44),
            height: rem(1023),
            backdropFilter: EDGE_BLUR,
            background: 'linear-gradient(269.236101549085deg, rgba(0,16,11,0.05) 11.832%, rgba(0,16,11,0) 76.903%)',
          }}
        />
      </div>

      {/* ---------------- Desktop · 12770:42308 (957x538) ---------------- */}
      <div className="absolute inset-0 hidden lg:block">
        <div className={cn('absolute inset-0', styles.artIn)}>
          {/* eslint-disable-next-line @next/next/no-img-element -- rasterised Figma layers */}
          <img
            src={`${BASE}/base-desktop.webp`}
            alt=""
            className="absolute inset-0 h-full w-full max-w-none object-cover"
          />
          <Cutout variant="desktop" />
        </div>
        {/* 12770:42329 escurecer área do texto */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, rgba(5,13,11,0) 0%, rgba(5,13,11,0.06) 40%, rgba(5,13,11,0.9) 55%, rgba(5,13,11,0.96) 100%)',
          }}
        />
        {/* 12770:42330 escurecer base: (0, 338) 957x200 */}
        <div
          className="absolute inset-x-0"
          style={{ top: rem(338), height: rem(200), background: 'linear-gradient(to bottom, rgba(5,13,11,0), rgba(5,13,11,0.75))' }}
        />
        {/* 12770:42354: blurred strip on the left edge, (-48, -88) 162x1022.538 */}
        <div
          className="absolute"
          style={{ left: rem(-48), top: rem(-88), width: rem(162), height: rem(1022.538), backdropFilter: EDGE_BLUR, background: EDGE_GRADIENT }}
        />
      </div>
    </>
  );
}
